import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';

import NavBar from '../navBar';
import ShowcaseBottomItems from '../bottom';
import {
  ShowCaseButtonsContainer,
  ShowcaseContainer,
  ShowcaseItemsContainer,
  ShowcaseTimelineButton,
  ShowcaseOverviewTitle,
  ShowcaseTextAndButtons,
  ShowCaseTopImageOpacity,
  ShowcaseImageAndTitle,
  ShowCaseHomeIcon,
} from './ShowcaseOverviewComponents';
import GridOn from '../../../assets/icons/GridOn.webp';
import GridOff from '../../../assets/icons/GridOff.webp';
import TimelineOn from '../../../assets/icons/TimelineOn.webp';
import TimelineOff from '../../../assets/icons/TimelineOff.webp';
import Home from '../../../assets/icons/hexmountain.png';
import ShowcaseGrid from './components/Grid';
import ShowCaseTimeline from './components/Timeline';
import { SHOWCASE, SHOWCASE_RESUME } from '../../../routes';
import { QuestApplet } from 'models/QuestApplet';
import ShowcaseModal from './components/ShowcaseModal';
import localizedStrings from 'localization';
import { selectShowcaseTitle } from '../selectors';
import { ShowcaseContent } from '../typings.d';
import { getShowcaseArtifact } from '../actions';
import useActionStatus from 'hooks/useActionStatus';
import LoadingScreenSimple from 'modules/loading-screen-simple';
import GenericErrorScreen from 'modules/generic-error-screen';

type ModalState = {
  open: boolean;
  applet?: QuestApplet;
};
interface ShowCaseResumeProps {
  image?: string;
}

const ShowcaseOverview = ({ image }: ShowCaseResumeProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [isSelectedGrid, setSelectedGrid] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({ open: false });

  const showcaseTitle = useSelector(selectShowcaseTitle);
  const [isLoading, wasFullfilled, wasRejected] = useActionStatus(getShowcaseArtifact);

  const changeToTimeline = useCallback(() => setSelectedGrid(false), []);
  const changeToGrid = useCallback(() => setSelectedGrid(true), []);

  const goToResume = useCallback(() => {
    history.push(`${SHOWCASE}/${id + SHOWCASE_RESUME}`);
  }, [history, id]);

  const openModal = (applet: QuestApplet) => {
    setModalState({ open: true, applet });
  };

  const closeModal = () => {
    setModalState({ open: false });
  };

  useEffect(() => {
    if (id && id !== '' && !wasFullfilled && !wasRejected) {
      dispatch(getShowcaseArtifact({ showcaseId: id, typeContent: ShowcaseContent.ARTIFACTS }));
    }
  }, [dispatch, id, wasFullfilled, wasRejected]);

  if (wasRejected) {
    return <GenericErrorScreen customMsg={localizedStrings.errorShowcase} />
  }

  if (isLoading) {
    return <LoadingScreenSimple />
  }

  return (
    <ShowcaseContainer>
      <NavBar />
      <ShowcaseItemsContainer>
        <ShowcaseImageAndTitle>
          <ShowcaseTextAndButtons image={image}>
            <ShowCaseTopImageOpacity />
            <ShowcaseOverviewTitle>
              <SplitTextSmart showcaseTitle={showcaseTitle} />
            </ShowcaseOverviewTitle>
            <ShowCaseButtonsContainer>
              <Tooltip title={localizedStrings.overview}>
                <ShowcaseTimelineButton
                  onClick={goToResume}
                  isSelectedGrid={false}>
                  <ShowCaseHomeIcon src={Home} alt="grid" />
                </ShowcaseTimelineButton>
              </Tooltip>
              <Tooltip title={localizedStrings.gridView}>
                <ShowcaseTimelineButton
                  onClick={changeToGrid}
                  isSelectedGrid={isSelectedGrid}>
                  <img src={isSelectedGrid ? GridOn : GridOff} alt="grid" />
                </ShowcaseTimelineButton>
              </Tooltip>
              <Tooltip title={localizedStrings.timelineView}>
                <ShowcaseTimelineButton
                  onClick={changeToTimeline}
                  isSelectedGrid={!isSelectedGrid}>
                  <img
                    src={isSelectedGrid ? TimelineOff : TimelineOn}
                    alt="timeline"
                  />
                </ShowcaseTimelineButton>
              </Tooltip>
            </ShowCaseButtonsContainer>
          </ShowcaseTextAndButtons>

        </ShowcaseImageAndTitle>

        <ShowcaseModal open={modalState.open} applet={modalState.applet} closeModal={closeModal} />
        {isSelectedGrid ? (
          <ShowcaseGrid onAppletClick={openModal} />
        ) : (
          <ShowCaseTimeline onAppletClick={openModal} />
        )}
        <ShowcaseBottomItems />
      </ShowcaseItemsContainer>
    </ShowcaseContainer>
  );
};

const SplitTextSmart = ({ showcaseTitle }: { showcaseTitle: string }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (windowWidth > 768 || showcaseTitle.split(' ').length <= 4) {
    return <>{showcaseTitle}</>;
  }

  const words = showcaseTitle.split(' ');
  const midIndex = Math.floor(words.length / 2);
  let bestSplit = midIndex;

  for (let i = midIndex; i < words.length - 1; i++) {
    if (words[i].length < 10) {
      bestSplit = i;
      break;
    }
  }

  const firstPart = words.slice(0, bestSplit + 1).join(' ');
  const secondPart = words.slice(bestSplit + 1).join(' ');

  return (
    <>
      {firstPart}
      <br />
      {secondPart}
    </>
  );
}
export default ShowcaseOverview;
